











































import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { Global, User, Groups, Reports, Props } from '@/store';
import Gmap from '@/pages/mapView/components/map/GMap.vue';
import CreateGroup from './CreateGroup.vue';
import API, { Types } from '@/modules/API';
import Utils from '@/modules/Utils';
import cloneDeep from 'lodash/cloneDeep';

@Component({
  components: {
    CreateGroup,
    Gmap
  }
})
class NewGroup extends Vue {
  @Global.State('lang') lang;
  @User.State('project') project;
  @Props.State('list') projectProperties;
  @Reports.State('reportsList') devices;
  @Reports.State('devicesCount') devicesCount;
  @Reports.Getter('commissionedFixtures') commissionedFixtures;
  @Reports.Getter('commissionedFixtures_map') commissionedFixtures_map;
  @Groups.Action('edit') edit;
  @Groups.State('cachedGroups') cachedGroups;
  @Groups.Mutation('cacheGroup') cacheGroup;

  groupDevices = new Map();
  createMode = false;
  group = null;
  loading = true;
  showMap = false;

  rules = {
    required: (value) => !!value || 'Required.'
  };

  async mounted() {
    await this.loadData();
  }

  @Watch('devices')
  @Watch('devicesCount')
  async loadData() {
    if (this.devices.length !== this.devicesCount) return;
    this.loading = true;
    this.showMap = false;
    this.groupDevices = new Map();
    this.createMode = !this.$route.params.id;
    if (!this.createMode) await this.setGroupData();
    this.loading = false;
    // Let the CreateGroup panel paint first, then mount the heavy GMap (renders
    // all commissioned fixtures). Deferring a frame keeps the page interactive
    // immediately instead of blocking on the marker build.
    this.$nextTick(() => requestAnimationFrame(() => (this.showMap = true)));
  }

  clear() {
    const map: any = this.$refs.map;
    if (map) {
      const devices = [...this.groupDevices.values()];
      if (devices.length) map['updateFeatureIconsBatch'](devices);
    }
    this.groupDevices = new Map();
  }

  async setGroupData() {
    const group_id = this.$route.params.id;
    // Reuse the group fetched by the display page if available, to skip the
    // redundant API round-trip when navigating display -> edit.
    const cached = this.cachedGroups && this.cachedGroups[group_id];
    this.group = cached || await API.get(Types.PROJECTS, `${this.project.id}/groups/${group_id}`, {}, {}, 'v4');
    if (!cached) this.cacheGroup(this.group);

    if (this.group && this.group.devices && this.group.devices.length) {
      this.group.devices.forEach((device_id) => {
        if (this.commissionedFixtures_map.has(device_id))
          this.groupDevices.set(device_id, this.commissionedFixtures_map.get(device_id));
      });
    }
  }

  updateSelectedDevices(devices, state, source?) {
    this.groupDevices = new Map(this.groupDevices);
    const changedDevices = [];
    // Single funnel for every selection source (map click, polygon, filter, list),
    // so gating additions here covers them all. Removals always stay allowed, so a
    // device that entered Retrofit after joining a group can still be taken out.
    let blocked = 0;

    // Only devices actually out for Retrofit are blocked. A pending-verification
    // device is already back (Delivered, waiting to reconnect), so it can join a
    // group - hence isDeviceUnderRetrofit rather than isDeviceLive.
    const addDevice = (device) => {
      if (Utils.isDeviceUnderRetrofit(device)) {
        blocked++;
        return;
      }
      this.groupDevices.set(device.id, device);
      changedDevices.push(device);
    };

    const removeDevice = (device) => {
      this.groupDevices.delete(device.id);
      changedDevices.push(device);
    };

    if (state === true) {
      devices.forEach((device) => {
        if (!this.groupDevices.has(device.id)) addDevice(device);
      });
    } else if (state === false) {
      devices.forEach((device) => {
        if (this.groupDevices.has(device.id)) removeDevice(device);
      });
    } else {
      devices.forEach((device) => {
        this.groupDevices.has(device.id) ? removeDevice(device) : addDevice(device);
      });
    }

    if (blocked) this.notifyRetrofitBlocked(blocked);

    const map: any = this.$refs.map;
    if (map && changedDevices.length) {
      map['updateFeatureIconsBatch'](changedDevices);
    }
    // When the selection comes from the filter, focus the map on all selected devices.
    if (source === 'filter' && map && this.groupDevices.size) {
      map['setBounds']([...this.groupDevices.values()]);
    }
  }
  selectAll(devices) {
    this.groupDevices = new Map();
    if (!devices.length) return;

    const selectable = devices.filter((device) => !Utils.isDeviceUnderRetrofit(device));
    selectable.forEach((device) => this.groupDevices.set(device.id, device));

    const blocked = devices.length - selectable.length;
    if (blocked) this.notifyRetrofitBlocked(blocked);
  }

  notifyRetrofitBlocked(count) {
    this.$store.dispatch('Global/throwNotify', {
      type: 'info',
      title: this.$t('Devices under Retrofit'),
      text: `${count} ${this.$t('devices are under Retrofit and cannot be added to a group until they return from Retrofit')}`
    });
  }
}

export default NewGroup;
